///
/// EarthBrowser
///
/// Created by Matt Giger on 12/21/2024
/// Copyright (c) 2024 Geodata Labs
///
/// The subscribe dialog, opened by every "unlock"/"subscribe" CTA
/// (dialogSlice.subscribeOpen). Shares its pitch copy with the
/// SupportInterstitial via SupportPitch so the two never drift apart.
///

import { SupportPitch, supportCtaButtonClass } from "@/components/shared/pricing/SupportPitch"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { canPurchaseInApp } from "@/native"
import { setSubscribeOpen } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useCreateCheckoutSessionMutation } from "@/redux/userApi"
import { logger } from "@/utils/LogDispatcher"
import { PricingPlans } from "@shared-types/pricing-plans"
import { XIcon } from "lucide-react"

export function SubscriptionDialog() {
	const dispatch = useAppDispatch()
	const subscribeOpen = useAppSelector(state => state.dialog.subscribeOpen)
	const userProfile = useAppSelector(state => state.user.userProfile)
	const [createCheckoutSession, { isLoading }] = useCreateCheckoutSessionMutation()

	// Go straight from the dialog to Stripe Checkout — no intermediate
	// /subscribe pitch page (that route remains the success/cancel redirect
	// target and the destination for signed-out email links).
	const handleCheckout = async () => {
		try {
			const { url } = await createCheckoutSession().unwrap()
			window.location.href = url
		} catch (error) {
			logger.error(error)
		}
	}

	if (!subscribeOpen || !userProfile) {
		return <></>
	}

	const plan = PricingPlans.yearly

	return (
		<Dialog modal open={subscribeOpen} onOpenChange={(open) => dispatch(setSubscribeOpen(open))}>
			<DialogContent
				className="flex flex-col gap-0 p-0 max-w-lg overflow-hidden rounded-3xl sm:rounded-3xl border-border/50 dark:border-border/50 bg-background/60 dark:bg-background/60 backdrop-blur-xl shadow-2xl"
				aria-describedby=""
			>
				<SupportPitch
					heading={
						<DialogTitle className="text-base font-bold text-foreground">
							Support EarthBrowser
						</DialogTitle>
					}
					actions={
						<>
							{canPurchaseInApp() ? (
								<Button
									onClick={() => void handleCheckout()}
									disabled={isLoading}
									className={supportCtaButtonClass}
								>
									{isLoading
										? "Starting checkout…"
										: `Subscribe — $${plan.price.toFixed(2)}/${plan.interval}`}
								</Button>
							) : (
								// Store policy: no web checkout inside the native apps.
								// In-app purchase replaces this once StoreKit / Play
								// Billing is wired up (see documentation/mobile)
								<p className="text-xs text-center text-muted-foreground px-4">
									In-app subscriptions are coming soon. Existing supporters can sign in to unlock everything.
								</p>
							)}
							<Button
								variant="ghost"
								onClick={() => dispatch(setSubscribeOpen(false))}
								className="rounded-full text-muted-foreground"
							>
								<XIcon className="h-4 w-4 mr-1" />
								Maybe later
							</Button>
						</>
					}
				/>
			</DialogContent>
		</Dialog>
	)
}
